import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import {
  ensureReferenceDataCached,
  fetchReferenceDataReservations,
  fetchAddressByPinCode,
  getApplicantFromStorage,
  getReferenceDataFromStorage,
  type ReferenceDataReservation,
  type ProfileUpdatePayload,
  updateProfile,
  useAuthState,
} from "../../hallticket-lib";
import { useAppLayout } from "../../layout";
import "./UpdateProfileForm.css";

const EWS_RESERVATION_NAME = "EWS";

const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Other",
];
function parseDateFromApi(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDateForApi(value: Date | null): string | null {
  if (!value) {
    return null;
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export type { ProfileUpdatePayload } from "../../hallticket-lib";

export interface ProfileDocumentCompletion {
  class10: boolean;
  class12: boolean;
  photoId: boolean;
  passportPhoto: boolean;
}

export interface UpdateProfileFormProps {
  onSuccess?: (payload: ProfileUpdatePayload) => void;
  onDocumentsChange?: (status: ProfileDocumentCompletion) => void;
}

export function UpdateProfileForm({ onSuccess }: UpdateProfileFormProps) {
  const { setLoader } = useAppLayout();
  const { applicant } = useAuthState();
  const [verticalReservationTypes, setVerticalReservationTypes] = useState<ReferenceDataReservation[]>([]);
  const [horizontalReservationTypes, setHorizontalReservationTypes] = useState<ReferenceDataReservation[]>([]);
  const [genderOptions, setGenderOptions] = useState<string[]>(() => getReferenceDataFromStorage()?.genders ?? []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [stateOfResidence, setStateOfResidence] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [category, setCategory] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postalInfoMessage, setPostalInfoMessage] = useState<string | null>(null);
  const [isPinLookupLoading, setIsPinLookupLoading] = useState(false);
  const [annualIncome, setAnnualIncome] = useState<"<8" | ">8" | null>(null);
  const [additionalReservations, setAdditionalReservations] = useState<string[]>([]);
  const ewsReservationName =
    horizontalReservationTypes.find((type) => type.name.trim().toUpperCase() === EWS_RESERVATION_NAME)?.name ??
    EWS_RESERVATION_NAME;

  useEffect(() => {
    const fallback = getApplicantFromStorage();
    const source = applicant ?? fallback;
    if (!source) {
      return;
    }
    setFirstName((prev) => prev || source.firstName || "");
    setLastName((prev) => prev || source.lastName || "");
    setGender((prev) => prev || source.gender || "");
    setDateOfBirth((prev) => prev ?? parseDateFromApi(source.dateOfBirth));
    setCategory((prev) => prev || source.verticalReservation || source.category || "");
    const reservations = source.horizontalReservations ?? [];
    setAdditionalReservations((prev) => (prev.length > 0 ? prev : reservations));
    if (reservations.some((item) => item.trim().toUpperCase() === EWS_RESERVATION_NAME)) {
      setAnnualIncome("<8");
    }
    setStateOfResidence((prev) => prev || source.stateOfResidence || "");
    setPinCode((prev) => prev || source.pinCode || "");
    setAddressLine1((prev) => prev || source.address || "");
    setCity((prev) => prev || source.city || "");
  }, [applicant]);

  useEffect(() => {
    if (!gender || genderOptions.length === 0) {
      return;
    }
    const match = genderOptions.find((option) => option.toLowerCase() === gender.toLowerCase());
    if (match && match !== gender) {
      setGender(match);
    }
  }, [gender, genderOptions]);

  useEffect(() => {
    let isMounted = true;
    const cachedReferenceData = getReferenceDataFromStorage();
    if (cachedReferenceData?.genders.length) {
      setGenderOptions(cachedReferenceData.genders);
    }

    void (async () => {
      try {
        const [reservations, referenceData] = await Promise.all([
          fetchReferenceDataReservations(),
          ensureReferenceDataCached(),
        ]);
        if (!isMounted) {
          return;
        }
        setVerticalReservationTypes(reservations.filter((item) => item.type === "Vertical"));
        setHorizontalReservationTypes(reservations.filter((item) => item.type === "Horizontal"));
        if (referenceData?.genders.length) {
          setGenderOptions(referenceData.genders);
        }
      } catch {
        if (!isMounted) {
          return;
        }
        setVerticalReservationTypes([]);
        setHorizontalReservationTypes([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (annualIncome === null) {
      return;
    }

    setAdditionalReservations((prev) => {
      const hasEws = prev.includes(ewsReservationName);
      if (annualIncome === "<8") {
        return hasEws ? prev : [...prev, ewsReservationName];
      }
      return hasEws ? prev.filter((item) => item !== ewsReservationName) : prev;
    });
  }, [annualIncome, ewsReservationName]);

  const handleLookupPin = async () => {
    if (!/^\d{6}$/.test(pinCode)) {
      setPostalInfoMessage("PIN code must be exactly 6 digits.");
      setIsPinLookupLoading(false);
      return;
    }

    setFieldError(null);
    setIsPinLookupLoading(true);
    setPostalInfoMessage(null);

    try {
      const result = await fetchAddressByPinCode(pinCode);
      const matchingState = STATES.find((state) => state.toLowerCase() === result.state.toLowerCase());
      const resolvedState = matchingState ?? result.state;
      setStateOfResidence(resolvedState);
      setCity(result.district || result.postOfficeName || "");

      const location = [result.postOfficeName, result.district].filter(Boolean).join(", ");
      setPostalInfoMessage(location ? `${location}, ${resolvedState}` : resolvedState);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not fetch address for this PIN code.";
      setPostalInfoMessage(message);
    } finally {
      setIsPinLookupLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    setSuccessMessage(null);
    setFieldError(null);

    const payload: ProfileUpdatePayload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      gender,
      dateOfBirth: formatDateForApi(dateOfBirth),
      stateOfResidence,
      stateOfDomicile: stateOfResidence,
      pinCode: pinCode.trim(),
      address: addressLine1.trim(),
      city: city.trim(),
      category,
      verticalReservation: category,
      horizontalReservations: additionalReservations,
    };

    if (!payload.firstName || !payload.lastName) {
      setFieldError("First name and last name are required.");
      return;
    }
    if (!/^\d{6}$/.test(payload.pinCode)) {
      setFieldError("PIN code must be exactly 6 digits.");
      return;
    }

    setIsSubmitting(true);
    setLoader(true);

    try {
      await updateProfile(payload);
      onSuccess?.(payload);
      const msg = "Your profile was updated successfully.";
      setSuccessMessage(msg);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setFieldError(message);
    } finally {
      setIsSubmitting(false);
      setLoader(false);
    }
  };

  const toggleAdditionalReservation = (item: string) => {
    if (annualIncome === "<8" && item === ewsReservationName) {
      return;
    }
    setAdditionalReservations((prev) =>
      prev.includes(item) ? prev.filter((value) => value !== item) : [...prev, item],
    );
  };

  return (
    <form className="update-profile-form" onSubmit={handleSubmit} noValidate>
      <section className="update-profile-form__panel">
        <h3 className="update-profile-form__title">Personal Details</h3>

        <div className="update-profile-form__row">
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-firstName">
              First name
            </label>
            <InputText
              id="up-firstName"
              className="update-profile-form__input p-inputtext-sm"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="e.g. Rahul"
              disabled={isSubmitting}
            />
          </div>
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-lastName">
              Last name
            </label>
            <InputText
              id="up-lastName"
              className="update-profile-form__input p-inputtext-sm"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="e.g. Sharma"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="update-profile-form__row">
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-dob">
              Date of birth
            </label>
            <Calendar
              inputId="up-dob"
              className="update-profile-form__input update-profile-form__calendar"
              panelClassName="update-profile-form__calendar-panel"
              panelStyle={{ width: "14rem" }}
              inputClassName="p-inputtext-sm"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth((e.value as Date | null) ?? null)}
              view="date"
              dateFormat="dd-mm-yy"
              placeholder="dd-mm-yyyy"
              showIcon
              iconPos="right"
              numberOfMonths={1}
              maxDate={new Date()}
              disabled={isSubmitting}
            />
          </div>
          <div className="update-profile-form__group">
            <label className="update-profile-form__label">
              Gender
            </label>
            <div className="update-profile-form__gender-control">
              <div className="update-profile-form__segment">
                {genderOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`update-profile-form__segment-btn${gender.toLowerCase() === option.toLowerCase() ? " active" : ""}`}
                    onClick={() => setGender(option)}
                    disabled={isSubmitting}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

      </section>

      <section className="update-profile-form__panel">
        <h3 className="update-profile-form__title">Address Details</h3>

        <div className="update-profile-form__row update-profile-form__row--single">
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-address-1">
              Flat / house no. & street
            </label>
            <InputText
              id="up-address-1"
              className="update-profile-form__input p-inputtext-sm"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="e.g. 12B, Rose Apartments, MG Road"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="update-profile-form__row update-profile-form__row--pin">
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-pin">
              PIN code
            </label>
            <InputText
              id="up-pin"
              className="update-profile-form__input p-inputtext-sm"
              value={pinCode}
              onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              keyfilter="int"
              maxLength={6}
              inputMode="numeric"
              placeholder="e.g. 411001"
              disabled={isSubmitting}
              autoComplete="postal-code"
            />
          </div>
          <div className="update-profile-form__pin-action">
            <Button
              type="button"
              label="Lookup →"
              size="small"
              loading={isPinLookupLoading}
              disabled={isSubmitting || isPinLookupLoading}
              onClick={() => void handleLookupPin()}
              className="update-profile-form__lookup"
            />
          </div>
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-city">
              City
            </label>
            <InputText
              id="up-city"
              className="update-profile-form__input p-inputtext-sm"
              value={city}
              placeholder="Auto-filled"
              disabled
            />
          </div>
        </div>

        <div className="update-profile-form__row update-profile-form__row--single">
          <div className="update-profile-form__group">
            <label className="update-profile-form__label" htmlFor="up-address-state">
              State (address)
            </label>
            <InputText
              id="up-address-state"
              className="update-profile-form__input p-inputtext-sm"
              value={stateOfResidence}
              placeholder="Auto-filled"
              disabled
            />
          </div>
        </div>

        <small className="update-profile-form__hint">
          {isPinLookupLoading ? "Fetching address..." : (postalInfoMessage ?? "\u00a0")}
        </small>
      </section>

      <section className="update-profile-form__panel">
        <div className="update-profile-form__head-row">
          <h3 className="update-profile-form__title">Reservation &amp; Eligibility Details</h3>
          <span className="update-profile-form__chip-tag">Affects seat eligibility in CAP rounds</span>
        </div>
        <div className="update-profile-form__info-strip">
          These details determine which seat categories you're eligible for during counselling.
        </div>

        <div className="update-profile-form__group update-profile-form__group--spaced">
          <div className="update-profile-form__label-row">
            <label className="update-profile-form__label">Caste category</label>
            <span className="update-profile-form__tag">Single select</span>
          </div>
          <div className="update-profile-form__pill-row">
            {verticalReservationTypes.map((reservationType) => (
              <button
                key={reservationType.id}
                type="button"
                className={`update-profile-form__pill${category === reservationType.name ? " active" : ""}`}
                onClick={() => setCategory(reservationType.name)}
                disabled={isSubmitting}
              >
                {reservationType.name}
              </button>
            ))}
          </div>
        </div>

        <div className="update-profile-form__group update-profile-form__group--spaced">
          <div className="update-profile-form__label-row">
            <label className="update-profile-form__label">Annual family income</label>
            <span className="update-profile-form__tag">Single select</span>
          </div>
          <div className="update-profile-form__pill-row">
            <button
              type="button"
              className={`update-profile-form__pill${annualIncome === "<8" ? " active" : ""}`}
              onClick={() => setAnnualIncome("<8")}
            >
              {'<= '}8 lakh p.a.
            </button>
            <button
              type="button"
              className={`update-profile-form__pill${annualIncome === ">8" ? " active" : ""}`}
              onClick={() => setAnnualIncome(">8")}
            >
              {'> '}8 lakh p.a.
            </button>
          </div>
        </div>

        <div className="update-profile-form__group update-profile-form__group--spaced">
          <div className="update-profile-form__label-row">
            <label className="update-profile-form__label">Additional reservations</label>
            <span className="update-profile-form__tag">Multi-select</span>
          </div>
          <div className="update-profile-form__reservation-grid">
            {horizontalReservationTypes.map((reservationType) => {
              const selected = additionalReservations.includes(reservationType.name);
              const isIncomeLockedEws = annualIncome === "<8" && reservationType.name === ewsReservationName;
              return (
                <button
                  key={reservationType.id}
                  type="button"
                  className={`update-profile-form__reservation-item${selected ? " active" : ""}`}
                  onClick={() => toggleAdditionalReservation(reservationType.name)}
                  disabled={isIncomeLockedEws}
                >
                  {reservationType.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <Button
        type="submit"
        label="Save Changes"
        size="small"
        loading={isSubmitting}
        disabled={isSubmitting}
        className="update-profile-form__submit"
      />

      {fieldError && <div className="update-profile-form__banner update-profile-form__banner--error">{fieldError}</div>}
      {successMessage && <div className="update-profile-form__success">{successMessage}</div>}
    </form>
  );
}
